import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fuzzyMatch, highlightParts, type FuzzyMatch } from '../fuzzy';
import { LANGUAGE_CHOICES } from '../languageCodes';

export function LanguagePicker({
  id,
  value,
  taken,
  label,
  disabled = false,
  onChange,
}: {
  id?: string;
  value: string;
  taken: string[];
  label: string;
  disabled?: boolean;
  onChange: (tag: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();

  const choices = useMemo(
    () =>
      [...new Set([...(value ? [value] : []), ...LANGUAGE_CHOICES])]
        .filter((tag) => tag === value || !taken.includes(tag))
        .sort((a, b) => a.localeCompare(b)),
    [value, taken],
  );

  const matches = useMemo(() => {
    if (!query.trim()) {
      return choices.map((tag) => ({ tag, match: null as FuzzyMatch | null }));
    }

    return choices
      .map((tag) => ({ tag, match: fuzzyMatch(query, tag) }))
      .filter((choice): choice is { tag: string; match: FuzzyMatch } => choice.match !== null)
      .sort((a, b) => b.match.score - a.match.score || a.tag.localeCompare(b.tag));
  }, [choices, query]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const dismiss = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, [open]);

  function show() {
    setQuery('');
    setActive(Math.max(0, choices.indexOf(value)));
    setOpen(true);
  }

  function take(tag: string | undefined) {
    if (tag) {
      onChange(tag);
    }

    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        if (!open) {
          show();
          return;
        }

        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActive((index) => Math.min(matches.length - 1, Math.max(0, index + step)));
        return;
      }

      case 'Enter':
        event.preventDefault();
        if (open) {
          take(matches[active]?.tag);
        } else {
          show();
        }

        return;

      case 'Escape':
        if (open) {
          event.preventDefault();
          setOpen(false);
          setQuery('');
        }

        return;

      case 'Tab':
        setOpen(false);
        setQuery('');
        return;

      default:
        return;
    }
  }

  return (
    <div className="picker picker--field language-picker" ref={root}>
      <input
        id={id}
        ref={input}
        type="text"
        className="form-control form-control-sm language-picker__input"
        role="combobox"
        aria-label={label}
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open}
        aria-activedescendant={open && matches[active] ? `${listId}-${active}` : undefined}
        placeholder={value || t('qrCode.languages.choose')}
        value={open ? query : value}
        disabled={disabled}
        onFocus={show}
        onClick={() => !open && show()}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
      />
      <i className="pi pi-search language-picker__icon" aria-hidden="true" />

      {open && (
        <ul className="picker__menu language-picker__menu" id={listId} role="listbox" aria-label={label}>
          {matches.length === 0 ? (
            <li className="picker__option language-picker__none" role="presentation">
              {t('qrCode.languages.noMatch')}
            </li>
          ) : (
            matches.map((choice, index) => (
              <li
                key={choice.tag}
                id={`${listId}-${index}`}
                role="option"
                aria-label={choice.tag}
                aria-selected={choice.tag === value}
                className={`picker__option${index === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => take(choice.tag)}
              >
                <span className="picker__value mono">
                  <Highlighted text={choice.tag} match={choice.match} />
                </span>
                {choice.tag === value && <i className="pi pi-check picker__tick" aria-hidden="true" />}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function Highlighted({ text, match }: { text: string; match: FuzzyMatch | null }) {
  if (!match || match.indices.length === 0) {
    return <>{text}</>;
  }

  return (
    <>
      {highlightParts(text, match.indices).map((part, index) =>
        part.matched ? (
          <mark key={index} className="language-picker__hit">
            {part.text}
          </mark>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}
